import { useCallback, useMemo } from 'react';
import startOfDay from 'date-fns/startOfDay';
import { type MenuItem } from '@direct-frontend/components';
import { compact } from '@direct-frontend/stdlib';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries -- подумать чуть позже
import { PROMOACTIONS_MODE } from '#entities/promoaction';
import { type OpenApiSchemaDiscount, type OpenApiSchemaPromocodeGroup } from '#shared/api/schema';
import { alertSrc } from '#shared/assets';
import { useConfirm } from '#shared/lib/hooks';
import { getPromoactionEditUrl } from '#shared/lib/routes';
import { showError, showSuccess } from '#shared/lib/toast';
import { addHttpsProtocol } from '#shared/lib/url';
import { changeDiscountDeactivated, useDeleteDiscount } from '../../api/api';

export interface DiscountMenuOptions {
    withEditLink?: boolean;
    onDeleted?: () => void;
    onStatusChanged?: (newDeactivated: OpenApiSchemaPromocodeGroup['deactivated']) => void;
}

type DiscountMenuDiscount = Pick<
    OpenApiSchemaDiscount,
    'id' | 'is_active' | 'title' | 'is_currently_active' | 'kind' | 'promo_offer_id' | 'discount_dates'
>;

/**
 * Сообщение об ошибке, когда скидку нельзя активировать из‑за срока действия.
 *
 * @param discount Данные скидки с датами и promo_offer_id.
 * @returns Текст ошибки для toast.
 * @kind helper
 */
function getExpiredDiscountErrorLabel(
    discount: Pick<OpenApiSchemaDiscount, 'promo_offer_id' | 'discount_dates'>
): string {
    const endDate = discount.discount_dates.end_date;
    const isEnded = endDate != null && startOfDay(new Date(endDate)).getTime() < startOfDay(new Date()).getTime();

    if (isEnded) {
        return discount.promo_offer_id
            ? 'Эта акция уже завершилась. Выберите другую рекомендацию'
            : 'Чтобы активировать скидку, измените её срок действия';
    }

    return 'Акция ещё не началась. Дождитесь даты старта';
}

export function useDiscountMenu(discount?: DiscountMenuDiscount, options: DiscountMenuOptions = {}) {
    const id = discount?.id ?? '';
    const is_active = discount?.is_active;
    const title = discount?.title;
    const is_currently_active = discount?.is_currently_active;
    const promo_offer_id = discount?.promo_offer_id;

    const { onDeleted, withEditLink, onStatusChanged } = options;
    const [fetchDeleteDiscount] = useDeleteDiscount();

    const { confirm, renderConfirmModal } = useConfirm();

    const editUrl = useMemo(
        () =>
            addHttpsProtocol(
                getPromoactionEditUrl({
                    id,
                    promoactionMode: PROMOACTIONS_MODE.automatic,
                    kind: discount?.kind,
                    promoOfferId: promo_offer_id,
                })
            ),
        [id, discount?.kind, promo_offer_id]
    );

    const handleStatusAction = useCallback(async () => {
        if (!discount) {
            return;
        }

        try {
            let isExpired = is_active && !is_currently_active;
            let expiredDiscount: Pick<OpenApiSchemaDiscount, 'promo_offer_id' | 'discount_dates'> = discount;

            // Если скидка истекла, то не нужно менять флаг deactivated
            if (!isExpired) {
                const newDiscount = await changeDiscountDeactivated({
                    params: { id },
                    payload: {
                        is_active: !is_active,
                    },
                });

                // Если после изменения флага скидка все еще is_currently_active, то срок действия истек
                if (!is_currently_active && !newDiscount.is_currently_active) {
                    isExpired = true;
                    expiredDiscount = newDiscount;
                } else {
                    onStatusChanged?.(Boolean(is_active));
                }
            }

            if (isExpired) {
                showError({ label: getExpiredDiscountErrorLabel(expiredDiscount) });
            } else {
                showSuccess(is_active ? 'Скидка деактивирована' : 'Скидка активирована');
            }
        } catch (e) {
            showError(
                {
                    label: is_active
                        ? 'Произошла ошибка при деактивации, попробуйте еще раз'
                        : 'Произошла ошибка при активации, попробуйте еще раз',
                },
                e
            );
        }
    }, [discount, id, is_active, is_currently_active, onStatusChanged]);

    const handleDeleteAction = useCallback(async () => {
        const isConfirmed = await confirm({
            title: `Удалить скидку «${title}»?`,
            applyText: 'Удалить',
            imageSrc: alertSrc,
        });

        if (!isConfirmed) {
            return;
        }

        try {
            await fetchDeleteDiscount({ id, promo_offer_id });
            showSuccess('Скидка удалена');
            onDeleted?.();
        } catch (e) {
            showError({ label: 'Произошла ошибка при удалении скидки, попробуйте еще раз' }, e);
        }
    }, [confirm, title, fetchDeleteDiscount, id, promo_offer_id, onDeleted]);

    const items = useMemo<MenuItem[]>(
        () =>
            discount
                ? compact([
                      {
                          key: 'main',
                          options: compact([
                              {
                                  value: 'status',
                                  content: is_currently_active ? 'Деактивировать' : 'Активировать',
                                  onAction: handleStatusAction,
                              },
                              withEditLink
                                  ? {
                                        key: 'edit',
                                        content: 'Редактировать',
                                        url: editUrl,
                                    }
                                  : null,
                          ]),
                      },
                      { key: 'delete', value: 'delete', content: 'Удалить', onAction: handleDeleteAction },
                  ])
                : [],
        [discount, is_currently_active, handleStatusAction, withEditLink, editUrl, handleDeleteAction]
    );

    return { items, renderConfirmModal };
}
